import React from 'react';
import { cn } from '../../lib/utils';

interface MarkdownTableProps {
  content: string;
  className?: string;
}

export const MarkdownTable: React.FC<MarkdownTableProps> = ({ content, className }) => {
  // Parse the markdown table
  const lines = content.trim().split('\n').filter(line => line.trim());
  if (lines.length < 2) return null;

  // Extract headers and rows
  const headers = lines[0].split('|').map(h => h.trim()).filter(h => h);
  const separatorLine = lines[1]; // This line contains |---|---|
  
  // Determine column alignments from separator line
  const alignments = separatorLine
    .split('|')
    .map(s => s.trim())
    .filter(s => s)
    .map(s => {
      if (s.startsWith(':') && s.endsWith(':')) return 'center';
      if (s.endsWith(':')) return 'right';
      if (s.startsWith(':')) return 'left';
      return 'left';
    });

  // Extract data rows
  const dataRows = lines.slice(2).map(line => 
    line.split('|').map(cell => cell.trim()).filter(cell => cell !== '')
  );

  return (
    <div className={cn("w-full overflow-x-auto my-6", className)}>
      <div className="inline-block min-w-full align-middle">
        <div className="overflow-hidden border border-gray-200 rounded-lg shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                {headers.map((header, index) => (
                  <th
                    key={index}
                    className="px-6 py-4 text-sm font-semibold text-gray-900 uppercase tracking-wider text-left"
                  >
                    {header.replace(/\*\*/g, '')}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dataRows.map((row, rowIndex) => (
                <tr key={rowIndex} className="hover:bg-gray-50 transition-colors">
                  {row.map((cell, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="px-6 py-4 text-sm text-gray-700 whitespace-normal text-left"
                    >
                      {/* Handle bold text */}
                      {cell.includes('**') ? (
                        <span
                          dangerouslySetInnerHTML={{
                            __html: cell.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          }}
                        />
                      ) : (
                        cell
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};